import { COUNTRIES } from "@/constants/countries";
import { DE_FEMALE_NAMES, DE_LAST_NAMES, DE_MALE_NAMES } from "@/constants/names/de";
import { ES_FEMALE_NAMES, ES_LAST_NAMES, ES_MALE_NAMES } from "@/constants/names/es";
import { FR_MALE_NAMES, FR_FEMALE_NAMES, FR_LAST_NAMES } from "@/constants/names/fr";
import { ID_FEMALE_NAMES, ID_LAST_NAMES, ID_MALE_NAMES } from "@/constants/names/id";
import { IN_FEMALE_NAMES, IN_LAST_NAMES, IN_MALE_NAMES } from "@/constants/names/in";
import { IT_FEMALE_NAMES, IT_LAST_NAMES, IT_MALE_NAMES } from "@/constants/names/it";
import { JP_FEMALE_NAMES, JP_LAST_NAMES, JP_MALE_NAMES } from "@/constants/names/jp";
import { KR_FEMALE_NAMES, KR_LAST_NAMES, KR_MALE_NAMES } from "@/constants/names/kr";
import { NL_FEMALE_NAMES, NL_LAST_NAMES, NL_MALE_NAMES } from "@/constants/names/nl";
import { PL_FEMALE_NAMES, PL_LAST_NAMES, PL_MALE_NAMES } from "@/constants/names/pl";
import { PT_FEMALE_NAMES, PT_LAST_NAMES, PT_MALE_NAMES } from "@/constants/names/pt";
import { RU_FEMALE_NAMES, RU_LAST_NAMES, RU_MALE_NAMES } from "@/constants/names/ru";
import { SA_FEMALE_NAMES, SA_LAST_NAMES, SA_MALE_NAMES } from "@/constants/names/sa";
import { TR_MALE_NAMES, TR_FEMALE_NAMES, TR_LAST_NAMES } from "@/constants/names/tr";
import { US_MALE_NAMES, US_FEMALE_NAMES, US_LAST_NAMES } from "@/constants/names/us";
import { VN_FEMALE_NAMES, VN_LAST_NAMES, VN_MALE_NAMES } from "@/constants/names/vn";
import { UK_FEMALE_NAMES, UK_LAST_NAMES, UK_MALE_NAMES } from "@/constants/names/uk";
import { AU_FEMALE_NAMES, AU_LAST_NAMES, AU_MALE_NAMES } from "@/constants/names/au";
import { CA_FEMALE_NAMES, CA_LAST_NAMES, CA_MALE_NAMES } from "@/constants/names/ca";

export interface NameGeneratorOptions {
  gender?: 'male' | 'female' | 'any';
  nameset?: string;
  nameType?: 'full' | 'first' | 'last';
  count?: number;
}

export interface GeneratedName {
  id: string;
  firstName: string;
  lastName: string;
  fullName: string;
  gender: 'male' | 'female';
  nameset: string;
}

// Country code to full name mapping
export const namesetLabels: Record<string, string> = Object.fromEntries(COUNTRIES.map((country) => [country.code, country.name]));

// Comprehensive name data for multiple countries
const nameData: Record<string, { male: string[]; female: string[]; lastNames: string[] }> = {
  us: {
    male: US_MALE_NAMES,
    female: US_FEMALE_NAMES,
    lastNames: US_LAST_NAMES,
  },
  tr: {
    male: TR_MALE_NAMES,
    female: TR_FEMALE_NAMES,
    lastNames: TR_LAST_NAMES,
  },
  es: {
    male: ES_MALE_NAMES,
    female: ES_FEMALE_NAMES,
    lastNames: ES_LAST_NAMES,
  },
  fr: {
    male: FR_MALE_NAMES,
    female: FR_FEMALE_NAMES,
    lastNames: FR_LAST_NAMES,
  },
  de: {
    male: DE_MALE_NAMES,
    female: DE_FEMALE_NAMES,
    lastNames: DE_LAST_NAMES,
  },
  pt: {
    male: PT_MALE_NAMES,
    female: PT_FEMALE_NAMES,
    lastNames: PT_LAST_NAMES,
  },
  sa: {
    male: SA_MALE_NAMES,
    female: SA_FEMALE_NAMES,
    lastNames: SA_LAST_NAMES,
  },
  in: {
    male: IN_MALE_NAMES,
    female: IN_FEMALE_NAMES,
    lastNames: IN_LAST_NAMES,
  },
  jp: {
    male: JP_MALE_NAMES,
    female: JP_FEMALE_NAMES,
    lastNames: JP_LAST_NAMES,
  },
  kr: {
    male: KR_MALE_NAMES,
    female: KR_FEMALE_NAMES,
    lastNames: KR_LAST_NAMES,
  },
  id: {
    male: ID_MALE_NAMES,
    female: ID_FEMALE_NAMES,
    lastNames: ID_LAST_NAMES,
  },
  vn: {
    male: VN_MALE_NAMES,
    female: VN_FEMALE_NAMES,
    lastNames: VN_LAST_NAMES,
  },
  ru: {
    male: RU_MALE_NAMES,
    female: RU_FEMALE_NAMES,
    lastNames: RU_LAST_NAMES,
  },
  it: {
    male: IT_MALE_NAMES,
    female: IT_FEMALE_NAMES,
    lastNames: IT_LAST_NAMES,
  },
  pl: {
    male: PL_MALE_NAMES,
    female: PL_FEMALE_NAMES,
    lastNames: PL_LAST_NAMES,
  },
  nl: {
    male: NL_MALE_NAMES,
    female: NL_FEMALE_NAMES,
    lastNames: NL_LAST_NAMES,
  },
  uk: {
    male: UK_MALE_NAMES,
    female: UK_FEMALE_NAMES,
    lastNames: UK_LAST_NAMES,
  },
  au: {
    male: AU_MALE_NAMES,
    female: AU_FEMALE_NAMES,
    lastNames: AU_LAST_NAMES,
  },
  ca: {
    male: CA_MALE_NAMES,
    female: CA_FEMALE_NAMES,
    lastNames: CA_LAST_NAMES,
  },
};

const allNamesets = Object.keys(nameData);

function randomItem<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}

export function generateNames(options: NameGeneratorOptions): GeneratedName[] {
  const {
    gender = 'any',
    nameset = 'any',
    nameType = 'full',
    count = 10,
  } = options;

  const namesets = nameset === 'any' ? allNamesets : [nameset];
  const results: GeneratedName[] = [];

  for (let i = 0; i < Math.min(count, 100); i++) {
    const chosenNameset = randomItem(namesets);
    const data = nameData[chosenNameset] || nameData['us'];
    let chosenGender: 'male' | 'female' = gender === 'any' ? randomItem(['male', 'female']) : gender;
    const firstName = randomItem(data[chosenGender]);
    const lastName = randomItem(data.lastNames);
    let fullName = '';
    if (nameType === 'full') fullName = `${firstName} ${lastName}`;
    else if (nameType === 'first') fullName = firstName;
    else if (nameType === 'last') fullName = lastName;
    results.push({
      id: `${chosenNameset}-${chosenGender}-${firstName}-${lastName}-${i}`,
      firstName,
      lastName,
      fullName,
      gender: chosenGender,
      nameset: chosenNameset,
    });
  }
  return results;
} 